/**
 * Une photo de boutique qui rate une fois ne doit pas rester cassée (17/09/2026).
 *
 * LE DÉFAUT. Sur la page produit de `ovfl-zonix.preprod`, la 2e vignette de la galerie peignait le carré
 * barré du navigateur avec « usecases-FINAL.png » écrit dedans. Le fichier était intact et se servait
 * normalement l'instant d'après : c'était un hoquet, et rien ne le rattrapait. Chaque image de la vitrine
 * est un aller-retour vers l'espace de stockage traversé par la JVM (pas de CDN devant), et une page
 * produit en déclenche une dizaine d'un coup — il suffit qu'une seule tombe.
 *
 * POURQUOI UN MOTEUR EN PLUS DE [ResilientImg]. Le composant React ne protège que ce qui est HYDRATÉ. Or
 * la vitrine ne monte qu'UN îlot par page : la galerie produit y est, mais les sections en rendu serveur
 * pur — galerie de fonctionnalités, collections, bandes d'images — n'ont pas une ligne de JavaScript à
 * elles. C'est vérifié, pas supposé : le parcours `product-image-recovery.spec.ts` a fait tomber onze
 * vignettes, sept se sont rétablies (celles de l'îlot) et quatre sont restées cassées, toutes dans
 * `feature-gallery`. Ce moteur est ce qui couvre les quatre.
 *
 * IL NE TOUCHE PAS AUX IMAGES DE REACT. Celles-là portent `data-aqx-img-react` : React est propriétaire de
 * leur `src`, et une URL réécrite à la main serait remise à l'ancienne au premier rendu suivant — le
 * précédent du chevron de la FAQ, où un moteur vanille écrasait ce que React venait de poser.
 *
 * L'ÉCHELLE, identique à celle du composant : la même URL avec un paramètre anti-cache (sinon le
 * navigateur rejoue son échec au lieu de refaire la requête), puis l'ORIGINAL sans `?w=` (au cas où ce
 * serait la variante qui manque), puis on s'arrête. Jamais de boucle.
 *
 * LE PIÈGE DU MOMENT. `error` ne se déclenche qu'une fois, et il a pu passer AVANT que ce module différé
 * n'arrive — c'est même le cas normal pour les images du haut de page, demandées pendant l'analyse du
 * HTML. `complete && naturalWidth === 0` est la façon de lire cet échec après coup. Sans elle, seules les
 * images `lazy` seraient rattrapées, et le défaut d'origine — une vignette du PREMIER écran — ne le
 * serait pas.
 *
 * CE QU'IL NE FAIT PAS : les photos d'avis. Elles sont souvent ÉTRANGÈRES (un lien d'import jamais
 * réhébergé), donc une qui ne répond pas ne répondra pas davantage à la deuxième tentative : elle est
 * retirée, pas retentée — voir `reviewPhotoInit`. Ici on ne s'occupe que de NOS octets.
 */
import { BROKEN_IMAGE_PLACEHOLDER, markBroken } from '../../blocks/_shared/brokenImage.js';
/** Nos propres URL de livraison — les seules dont on sait qu'un original existe derrière la variante. */
const OUR_ASSETS = '/api/assets/public/';
/** Le délai avant de retenter : invisible pour l'acheteuse, suffisant pour laisser passer un 503. */
const RETRY_DELAY_MS = 600;
const ATTEMPT = 'aqxImgTry';
/** L'URL que le moteur a posée lui-même, pour la reconnaître quand le DOM rebouge. */
const WRITTEN = 'aqxImgSet';
/** Une tentative déjà programmée sur cette image : le 2e signalement du même échec ne compte pas. */
const PENDING = 'aqxImgPending';
/** Le compteur d'essais de l'affiche d'une vidéo — même échelle, autre attribut. */
const POSTER_ATTEMPT = 'aqxPosterTry';
/** L'affiche que le moteur a posée lui-même. Même rôle que [WRITTEN] pour les images. */
const POSTER_WRITTEN = 'aqxPosterSet';
/** La même URL, mais que le navigateur ne peut pas confondre avec celle qu'il vient de rater. */
function bust(url) {
    return `${url}${url.includes('?') ? '&' : '?'}aqxr=1`;
}
/** L'original derrière une variante : `…webp?w=160` → `…webp`. */
function original(url) {
    return url.split('?')[0] ?? url;
}
/**
 * Faire remonter l'image d'un cran. `srcSet` doit partir en même temps, sinon il l'emporte sur le `src`
 * de secours et le navigateur redemande exactement la variante qui vient de rater.
 */
function recover(image) {
    // UNE SEULE TENTATIVE EN VOL (défaut trouvé au parcours navigateur du 18/09/2026). Une image tombée
    // se signale DEUX fois : l'`error` du navigateur, et la relecture `complete && naturalWidth === 0`
    // faite au branchement pour rattraper l'échec qu'on n'a pas entendu. Sans ce verrou les deux appels
    // programment chacun leur barreau, les deux minuteries arrivent ensemble, et le dernier gagne : un
    // hoquet unique brûlait l'échelle entière — mesuré, la photo repartait directement sur l'ORIGINAL
    // sans jamais essayer la simple URL anti-cache, qui est pourtant celle qui suffit. [ResilientImg]
    // avait ce verrou depuis le premier jour ; le moteur vanille, non.
    if (image.dataset[PENDING] === 'true')
        return;
    const attempt = Number(image.dataset[ATTEMPT] ?? '0');
    const current = image.getAttribute('src') ?? '';
    if (current === '')
        return;
    // L'échelle est épuisée : on n'essaie plus rien, mais on ne laisse pas le carré barré non plus
    // (règle de la fondatrice, 23/09/2026). Le cadre neutre est signé comme une URL à nous, pour que le
    // veilleur ne prenne pas ce remplacement pour une nouvelle image à remettre à zéro.
    if (attempt >= 2) {
        giveUp(image);
        return;
    }
    const next = attempt === 0 ? bust(current) : original(current);
    // Le repli vers l'original n'a de sens que s'il diffère : sans variante en jeu, on s'arrête là.
    if (next === current) {
        image.dataset[ATTEMPT] = '2';
        giveUp(image);
        return;
    }
    image.dataset[ATTEMPT] = String(attempt + 1);
    image.dataset[PENDING] = 'true';
    window.setTimeout(() => {
        image.dataset[PENDING] = 'false';
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        // Signer ce qu'on écrit : le veilleur ci-dessous doit pouvoir distinguer « c'est moi qui viens de
        // retenter » de « React a reposé une autre URL », sans quoi il remettrait l'échelle à zéro à chaque
        // essai et la boucle serait infinie.
        image.dataset[WRITTEN] = next;
        image.setAttribute('src', next);
    }, RETRY_DELAY_MS);
}
/** Plus d'essai : le cadre neutre à la place du carré barré, signé pour que le veilleur le laisse. */
function giveUp(image) {
    if (image.getAttribute('src') === BROKEN_IMAGE_PLACEHOLDER)
        return;
    image.dataset[WRITTEN] = BROKEN_IMAGE_PLACEHOLDER;
    markBroken(image);
}
/** Surveille les images servies par nous sous [root]. Rend le démontage des écouteurs posés. */
export function initImageRecovery(root) {
    const cleanups = [];
    for (const image of Array.from(root.querySelectorAll('img'))) {
        // React est propriétaire de son `src` : lui écrire dessus serait écrasé au rendu suivant.
        if (image.dataset.aqxImgReact !== undefined)
            continue;
        // Les photos d'avis ont déjà leur moteur, qui les RETIRE au lieu de les retenter (un lien d'import
        // mort ne répondra pas davantage au 2e essai). Deux moteurs sur la même image, c'est l'un qui écrit
        // un `src` sur un nœud que l'autre vient de détacher — voir `reviewPhotoInit`.
        if (image.dataset.aqxReviewPhoto !== undefined)
            continue;
        if (image.dataset.aqxImgBound === 'true')
            continue;
        // Une image ÉTRANGÈRE (URL collée par la vendeuse) n'a pas d'original à nous derrière elle : pas
        // d'échelle, mais pas de carré barré non plus — elle passe directement au cadre neutre.
        if (!(image.getAttribute('src') ?? '').includes(OUR_ASSETS)) {
            image.dataset.aqxImgBound = 'true';
            const onForeignError = () => giveUp(image);
            if (image.complete && image.naturalWidth === 0 && (image.getAttribute('src') ?? '') !== '')
                giveUp(image);
            image.addEventListener('error', onForeignError);
            cleanups.push(() => {
                image.removeEventListener('error', onForeignError);
                delete image.dataset.aqxImgBound;
            });
            continue;
        }
        image.dataset.aqxImgBound = 'true';
        // Déjà tombée avant l'arrivée du script : `complete` sans le moindre pixel décodé.
        if (image.complete && image.naturalWidth === 0)
            recover(image);
        const onError = () => recover(image);
        image.addEventListener('error', onError);
        cleanups.push(() => {
            image.removeEventListener('error', onError);
            // Délier, c'est aussi retirer la marque : sans ça un re-balayage (le veilleur du builder en fait
            // un par image écran) prendrait l'image pour déjà surveillée et la laisserait SANS écouteur —
            // un filet qui se retire tout seul au premier remontage de section.
            delete image.dataset.aqxImgBound;
        });
    }
    return () => cleanups.forEach((cleanup) => cleanup());
}
/**
 * L'AFFICHE D'UNE VIDÉO QUI RATE PEINT DU NOIR (18/09/2026).
 *
 * Une `<img>` tombée laisse un carré barré : on la voit, et c'est laid. Une `<video preload="none">`
 * dont l'affiche n'arrive pas, elle, ne laisse RIEN — le navigateur peint la boîte en **noir plein**.
 * C'est la même panne que celle des photos, mais elle ne ressemble pas à une panne : ça ressemble à une
 * section vide, et on cherche le défaut dans les réglages au lieu de recharger la page.
 *
 * Le navigateur n'émet aucun `error` pour une affiche : elle n'est pas un élément, juste un attribut. La
 * seule façon de savoir qu'elle est tombée est de redemander la même URL dans une `Image()` — un échec
 * n'étant pas mis en cache, la sonde repart vraiment sur le réseau, alors qu'une affiche saine se
 * résout depuis le cache sans un octet de plus.
 *
 * On ne sonde pas une vidéo qui a déjà une image à elle (`readyState >= HAVE_CURRENT_DATA`) : son
 * affiche ne se voit plus, la réparer ne réparerait rien de visible.
 */
export function initPosterRecovery(root) {
    const probes = [];
    for (const video of Array.from(root.querySelectorAll('video[poster]'))) {
        if (video.dataset.aqxPosterBound === 'true')
            continue;
        const poster = video.getAttribute('poster') ?? '';
        // Comme pour les images : seulement NOS octets. Une affiche étrangère n'a pas d'original derrière
        // elle, et si elle ne répond pas elle ne répondra pas mieux au 2e essai.
        if (!poster.includes(OUR_ASSETS))
            continue;
        // Elle joue déjà — l'affiche est passée sous la vidéo, il n'y a plus de noir à réparer.
        if (video.readyState >= 2)
            continue;
        video.dataset.aqxPosterBound = 'true';
        const attempt = Number(video.dataset[POSTER_ATTEMPT] ?? '0');
        if (attempt >= 2)
            continue;
        const probe = new Image();
        probes.push(probe);
        probe.addEventListener('error', () => {
            const current = video.getAttribute('poster') ?? '';
            const next = attempt === 0 ? bust(current) : original(current);
            if (next === current) {
                video.dataset[POSTER_ATTEMPT] = '2';
                return;
            }
            video.dataset[POSTER_ATTEMPT] = String(attempt + 1);
            video.dataset[POSTER_WRITTEN] = next;
            window.setTimeout(() => {
                // Re-sonder après coup : `poster` n'est relu par le navigateur que s'il CHANGE, donc la
                // nouvelle URL suffit à déclencher une vraie deuxième demande.
                video.dataset.aqxPosterBound = 'false';
                video.setAttribute('poster', next);
                initPosterRecovery(video.parentNode ?? root);
            }, RETRY_DELAY_MS);
        });
        probe.src = poster;
    }
    // Une sonde en vol garde une requête ouverte ; au démontage on la coupe net.
    return () => probes.forEach((probe) => (probe.src = ''));
}
/**
 * LE MÊME FILET, MAIS DANS UN ÉDITEUR (18/09/2026, 2e signalement de la fondatrice).
 *
 * `initImageRecovery` est un BALAYAGE : il regarde les images présentes à l'instant où on l'appelle. Sur
 * la vitrine c'est exact — le HTML est servi d'un bloc et ne bouge plus. Dans le builder c'est faux la
 * seconde d'après : le canvas remonte des sections à chaque réglage, chaque glisser-déposer, chaque
 * changement de page, et toutes ces images-là seraient nées sans filet.
 *
 * D'où un VEILLEUR plutôt qu'un balayage : on re-balaie quand le DOM bouge. Deux précautions payées par
 * l'expérience :
 *
 *  - **Coalescer.** Un glisser-déposer produit des centaines de mutations ; re-balayer à chacune ferait
 *    payer l'édition. Une seule passe par image écran suffit — l'échelle a de toute façon 600 ms devant
 *    elle.
 *  - **Repartir à zéro quand l'URL change VRAIMENT.** Changer « Largeur » ou passer en partie Mobile fait
 *    poser à React un autre palier `?w=` : c'est une nouvelle image, elle a droit à une nouvelle échelle.
 *    Sans ce remise à zéro, une photo ayant épuisé ses essais resterait condamnée pour toute la session
 *    d'édition. Avec, mais sans la signature `aqxImgSet`, le moteur remettrait le compteur à zéro sur ses
 *    PROPRES écritures et retenterait sans fin.
 */
export function watchMediaRecovery(root) {
    let offImages = initImageRecovery(root);
    let offPosters = initPosterRecovery(root);
    let scheduled = 0;
    const rescan = () => {
        scheduled = 0;
        offImages();
        offPosters();
        offImages = initImageRecovery(root);
        offPosters = initPosterRecovery(root);
    };
    const observer = new MutationObserver((records) => {
        for (const record of records) {
            if (record.type !== 'attributes')
                continue;
            const node = record.target;
            if (record.attributeName === 'src' && node instanceof HTMLImageElement) {
                const now = node.getAttribute('src') ?? '';
                // Notre propre nouvelle tentative : surtout ne pas rouvrir l'échelle, ce serait une boucle.
                if (now === node.dataset[WRITTEN])
                    continue;
                delete node.dataset[ATTEMPT];
                delete node.dataset[WRITTEN];
                delete node.dataset[PENDING];
                delete node.dataset.aqxImgBound;
            }
            else if (record.attributeName === 'poster' && node instanceof HTMLVideoElement) {
                const now = node.getAttribute('poster') ?? '';
                if (now === node.dataset[POSTER_WRITTEN])
                    continue;
                delete node.dataset[POSTER_ATTEMPT];
                delete node.dataset[POSTER_WRITTEN];
                delete node.dataset.aqxPosterBound;
            }
        }
        if (scheduled !== 0)
            return;
        scheduled = window.requestAnimationFrame(rescan);
    });
    observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'poster'],
    });
    return () => {
        observer.disconnect();
        if (scheduled !== 0)
            window.cancelAnimationFrame(scheduled);
        offImages();
        offPosters();
    };
}
//# sourceMappingURL=imageRecoveryInit.js.map